import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TablePagination,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import { useGetPurchaseNotesQuery, useDeletePurchaseNoteMutation } from "../../../store/api/purchaseApi";
import { useGetCurrentCompanyQuery } from "../../../store/api/companyApi";

function PurchaseNotesList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const toApiDate = (d) =>
    d instanceof Date && !Number.isNaN(d.getTime())
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
          d.getDate(),
          10,
        ).padStart(2, "0")}`
      : undefined;
  const [deleteDialogNote, setDeleteDialogNote] = useState(null);
  const [deletePurchaseNote, { isLoading: isDeleting }] = useDeletePurchaseNoteMutation();
  const { data: notes, isLoading, error } = useGetPurchaseNotesQuery({
    page,
    limit: rowsPerPage,
    status: statusFilter || undefined,
    search: search || undefined,
    fromDate: toApiDate(fromDate),
    toDate: toApiDate(toDate),
  });
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || "USD";
  const dateFormat = company?.dateFormat || "dd/MM/yyyy";

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed((company?.currencyDecimals ?? 2))}`;
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    switch (dateFormat) {
      case "MM/dd/yyyy": return `${mm}/${dd}/${yyyy}`;
      case "yyyy-MM-dd": return `${yyyy}-${mm}-${dd}`;
      case "dd-MM-yyyy": return `${dd}-${mm}-${yyyy}`;
      default: return `${dd}/${mm}/${yyyy}`;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      converted: "success",
      cancelled: "error",
    };
    return colors[status] || "default";
  };

  if (isLoading) return <Typography sx={{ p: 4 }}>Loading...</Typography>;
  if (error) return <Typography color="error" sx={{ p: 4 }}>Error loading purchase notes</Typography>;

  const rows = notes || [];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Purchase Notes
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/purchases/notes/new")}
            sx={{ borderRadius: 2 }}
          >
            New Purchase Note
          </Button>
        </Box>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Card>
          <CardContent>
            <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <TextField
                  size="small"
                  label="Search (Note # / Supplier)"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  sx={{ minWidth: 220 }}
                />
                <TextField
                  size="small"
                  select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </TextField>
                <DatePicker
                  label="From date"
                  value={fromDate}
                  onChange={(newValue) => {
                    setFromDate(newValue);
                    setPage(1);
                  }}
                  slotProps={{ textField: { size: "small" } }}
                />
                <DatePicker
                  label="To date"
                  value={toDate}
                  onChange={(newValue) => {
                    setToDate(newValue);
                    setPage(1);
                  }}
                  slotProps={{ textField: { size: "small" } }}
                />
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Note Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Supplier</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Total Amount (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length > 0 ? (
                    rows.map((note) => (
                      <TableRow key={note.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{note.noteNumber}</TableCell>
                        <TableCell>{note.supplier?.name || "-"}</TableCell>
                        <TableCell>{formatDate(note.noteDate)}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "primary.main" }}>
                          {formatAmount(note.grandTotal)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={note.status}
                            color={getStatusColor(note.status)}
                            size="small"
                            sx={{ textTransform: "capitalize", fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View/Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/purchases/notes/${note.id}/edit`)}
                            >
                              {note.status === 'converted' ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          {note.status === 'draft' && (
                            <Tooltip title="Convert to Purchase">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => navigate(`/purchases/orders/new?noteId=${note.id}`)}
                              >
                                <SyncIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialogNote(note)}
                              disabled={note.status === 'converted'}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No purchase notes found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </LocalizationProvider>

      <Dialog open={!!deleteDialogNote} onClose={() => setDeleteDialogNote(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete purchase note?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this purchase note? This action cannot be undone.</Typography>
          {deleteDialogNote && (
            <Typography sx={{ mt: 1, fontWeight: 600 }}>Note: {deleteDialogNote.noteNumber}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogNote(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={isDeleting}
            onClick={async () => {
              try {
                await deletePurchaseNote(deleteDialogNote.id).unwrap();
                setDeleteDialogNote(null);
              } catch (err) {
                alert(err?.data?.message || "Failed to delete purchase note");
              }
            }}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PurchaseNotesList;
